import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { getHighLevelContext } from '@/utils/highlevel';
import toast from 'react-hot-toast';

interface UseSyncStatusOptions {
  enabled?: boolean;
}

export function useSyncStatus(options: UseSyncStatusOptions = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const context = getHighLevelContext();
  const locationId = context?.locationId || '';

  // Only enable queries when both enabled option is true AND we have a locationId
  const shouldFetch = enabled && !!locationId;

  // Get sync stats
  const {
    data: stats,
    isLoading: isLoadingStats,
    error: statsError,
  } = useQuery({
    queryKey: ['sync-stats', locationId],
    queryFn: () => apiClient.getSyncStats(locationId),
    enabled: shouldFetch,
    refetchInterval: shouldFetch ? 10000 : false, // Refetch every 10 seconds only when enabled
  });

  // Get sync jobs/history
  const {
    data: jobsData,
    isLoading: isLoadingJobs,
    error: jobsError,
  } = useQuery({
    queryKey: ['sync-jobs', locationId],
    queryFn: () => apiClient.getSyncJobs(locationId, 20, 0),
    enabled: shouldFetch,
    refetchInterval: shouldFetch ? 5000 : false, // Refetch every 5 seconds for active jobs only when enabled
  });

  // Trigger manual sync
  const triggerSyncMutation = useMutation({
    mutationFn: (entityType?: 'contact' | 'product' | 'invoice') =>
      apiClient.triggerSync(locationId, entityType),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sync-jobs', locationId] });
      queryClient.invalidateQueries({ queryKey: ['sync-stats', locationId] });
      toast.success(
        `Sync started for ${data.entityType || 'all entities'}`,
        {
          icon: '🔄',
        }
      );
    },
    onError: (error) => {
      toast.error(`Failed to start sync: ${error.message}`);
    },
  });

  const hasActiveJobs =
    jobsData?.jobs?.some(
      (job) => job.status === 'running' || job.status === 'pending'
    ) || false;

  return {
    stats,
    jobs: jobsData?.jobs || [],
    totalJobs: jobsData?.total || 0,
    isLoading: isLoadingStats || isLoadingJobs,
    error: statsError || jobsError,
    triggerSync: triggerSyncMutation.mutate,
    isSyncing: triggerSyncMutation.isPending || hasActiveJobs,
  };
}
